import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./guidedTours.css";
import AuthService from "../../../service/auth.service";
import { useFeatureFlags } from "../../../context/FeatureFlagContext";
import { TOUR_JOURNEYS, getJourneyById, getTourById, getTourForPath } from "./tourRegistry";

const TOUR_STORAGE_KEY = "typogram_guided_tours_v1";
const JOURNEY_STORAGE_KEY = "typogram_guided_journeys_v1";
const GUIDED_TOURS_FIRST_VISIT_FLAG = "guided_tours_first_visit";
const GuidedTourContext = createContext(null);

function loadTourState() {
  try {
    return JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function loadJourneyState() {
  try {
    return JSON.parse(localStorage.getItem(JOURNEY_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function waitForElements(selectors, timeoutMs = 10000) {
  const requiredSelectors = selectors.filter(Boolean);
  if (requiredSelectors.length === 0) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
      const ready = requiredSelectors.every((selector) => document.querySelector(selector));
      if (ready) {
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(check, 120);
    };

    check();
  });
}

export function GuidedTourProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();
  const [tourState, setTourState] = useState(loadTourState);
  const [journeyState, setJourneyState] = useState(loadJourneyState);
  const [activeTourId, setActiveTourId] = useState(null);
  const [debugInfo, setDebugInfo] = useState(() => ({
    lastAction: "idle",
    detail: "",
  }));
  const autoStartedRef = useRef({});
  const driverRef = useRef(null);
  const destroyReasonRef = useRef(null);
  const suppressStageActionRef = useRef(null);
  const currentUser = AuthService.getCurrentUser();
  const showDebugOverlay =
    typeof window !== "undefined" && window.location.hostname === "localhost";
  const firstVisitToursEnabled = featureFlags[GUIDED_TOURS_FIRST_VISIT_FLAG] !== false;

  const currentTour = useMemo(() => getTourForPath(location.pathname), [location.pathname]);
  const activeJourney = useMemo(() => {
    const entry = Object.entries(journeyState).find(([, value]) => value?.status === "active");
    if (!entry) {
      return null;
    }

    const [journeyId, state] = entry;
    const journey = getJourneyById(journeyId);
    if (!journey) {
      return null;
    }

    return { journey, state };
  }, [journeyState]);

  const persistTourState = useCallback((tourId, status) => {
    setTourState((prev) => {
      const next = {
        ...prev,
        [tourId]: {
          status,
          updatedAt: new Date().toISOString(),
        },
      };
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const persistJourneyState = useCallback((journeyId, patch) => {
    setJourneyState((prev) => {
      const previous = prev[journeyId] || {};
      const next = {
        ...prev,
        [journeyId]: {
          ...previous,
          ...patch,
          updatedAt: new Date().toISOString(),
        },
      };
      localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearJourneyState = useCallback((journeyId) => {
    setJourneyState((prev) => {
      const next = { ...prev };
      delete next[journeyId];
      localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearJourneyTourStatuses = useCallback((journeyId) => {
    const journey = getJourneyById(journeyId);
    if (!journey) {
      return;
    }

    setTourState((prev) => {
      const next = { ...prev };
      journey.stages.forEach((stage) => {
        delete next[stage.tourId];
      });
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const teardownTour = useCallback(() => {
    if (driverRef.current) {
      destroyReasonRef.current = "manual";
      driverRef.current.destroy();
      driverRef.current = null;
    }
    setActiveTourId(null);
  }, []);

  const setDebug = useCallback((lastAction, detail = "") => {
    setDebugInfo({
      lastAction,
      detail,
    });
  }, []);

  const runJourneyStageAction = useCallback((action) => {
    if (!action) {
      setDebug("stage-action", "no action");
      return;
    }

    if (action.type === "click" && action.selector) {
      const target = document.querySelector(action.selector);
      if (target instanceof HTMLElement) {
        setDebug("stage-action:click", action.selector);
        target.click();
      } else {
        setDebug("stage-action:click-miss", action.selector);
      }
      return;
    }

    if (action.type === "navigate-attr" && action.selector && action.attribute) {
      const target = document.querySelector(action.selector);
      if (target instanceof HTMLElement) {
        const nextPath = target.getAttribute(action.attribute);
        if (nextPath) {
          setDebug("stage-action:navigate-attr", `${action.selector} -> ${nextPath}`);
          navigate(nextPath);
        } else {
          setDebug("stage-action:navigate-attr-miss", `${action.selector} missing ${action.attribute}`);
        }
      } else {
        setDebug("stage-action:navigate-attr-miss", action.selector);
      }
      return;
    }

    if (action.type === "navigate-storage" && action.key) {
      const nextPath = localStorage.getItem(action.key);
      if (nextPath) {
        setDebug("stage-action:navigate-storage", `${action.key} -> ${nextPath}`);
        navigate(nextPath);
      } else {
        setDebug("stage-action:navigate-storage-miss", action.key);
      }
    }
  }, [navigate, setDebug]);

  const startTour = useCallback(async (tourId, options = {}) => {
    const tour = getTourById(tourId);
    if (!tour) {
      return false;
    }

    const stepSelectors = tour.steps.map((step) => step.element).filter(Boolean);
    const ready = await waitForElements(stepSelectors);
    if (!ready) {
      return false;
    }

    if (driverRef.current?.isActive()) {
      destroyReasonRef.current = "replace";
      driverRef.current.destroy();
    }

    let completed = false;
    const stepCount = tour.steps.length;
    const lastStep = tour.steps[stepCount - 1];
    const finalButtonText =
      options.journeyId || lastStep?.continueTo || lastStep?.continueAction
        ? "Next"
        : "Done";

    const instance = driver({
      animate: true,
      allowClose: true,
      overlayOpacity: 0.78,
      overlayColor: "#030712",
      stagePadding: 10,
      stageRadius: 18,
      smoothScroll: true,
      showProgress: true,
      popoverOffset: 16,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: finalButtonText,
      popoverClass: "typogram-driver-popover",
      onDestroyed: () => {
        const destroyReason = destroyReasonRef.current;
        destroyReasonRef.current = null;
        driverRef.current = null;
        setActiveTourId(null);
        if (
          !completed &&
          destroyReason !== "replace" &&
          destroyReason !== "route-change" &&
          destroyReason !== "programmatic" &&
          !options.force &&
          !tourState[tourId]?.status
        ) {
          persistTourState(tourId, "skipped");
        }
      },
    });

    const steps = tour.steps.map((step, index) => ({
      element: step.element,
      disableActiveInteraction: step.disableActiveInteraction ?? false,
      popover: {
        title: step.title,
        description: step.description,
        side: step.side || "bottom",
        align: step.align || "center",
        showButtons:
          index === 0
            ? ["next", "close"]
            : index === stepCount - 1
              ? ["previous", "next", "close"]
              : ["previous", "next", "close"],
        onPopoverRender: () => {},
        onNextClick: (_element, _step, popoverOpts) => {
          if (index === stepCount - 1) {
            completed = true;
            persistTourState(tourId, "seen");

            if (step.continueAction) {
              suppressStageActionRef.current = options.journeyId && options.stageIndex != null
                ? `${options.journeyId}:${options.stageIndex}`
                : null;
            }

            destroyReasonRef.current = "programmatic";
            popoverOpts.driver.destroy();

            if (step.continueTo) {
              navigate(step.continueTo.path, step.continueTo.navigateOptions || undefined);
            }

            if (step.continueAction) {
              runJourneyStageAction(step.continueAction.action);
            }

            window.setTimeout(() => {
              options.onDone?.();
            }, 0);
            return;
          }
          popoverOpts.driver.moveNext();
        },
        onPrevClick: (_element, _step, popoverOpts) => {
          popoverOpts.driver.movePrevious();
        },
        onDoneClick: (_element, _step, popoverOpts) => {
          completed = true;
          persistTourState(tourId, "seen");
          destroyReasonRef.current = "programmatic";
          popoverOpts.driver.destroy();
          window.setTimeout(() => {
            options.onDone?.();
          }, 0);
        },
        onCloseClick: (_element, _step, popoverOpts) => {
          if (options.journeyId) {
            persistJourneyState(options.journeyId, { status: "dismissed" });
          }
          if (!options.force) {
            persistTourState(tourId, "skipped");
          }
          destroyReasonRef.current = "programmatic";
          popoverOpts.driver.destroy();
        },
      },
    }));

    instance.setSteps(steps);
    driverRef.current = instance;
    setActiveTourId(tourId);

    window.requestAnimationFrame(() => {
      instance.drive();
    });

    return true;
  }, [navigate, persistJourneyState, persistTourState, runJourneyStageAction, tourState]);

  const startJourney = useCallback((journeyId, options = {}) => {
    const journey = getJourneyById(journeyId);
    if (!journey) {
      return false;
    }

    if (options.force) {
      clearJourneyTourStatuses(journeyId);
      clearJourneyState(journeyId);
    }

    const startIndex = Number.isInteger(options.startIndex) ? options.startIndex : 0;
    persistJourneyState(journeyId, {
      status: "active",
      currentStageIndex: startIndex,
    });
    setDebug("journey-start", `${journeyId} @ stage ${startIndex}`);

    const stage = journey.stages[startIndex];
    if (stage?.nextPath && !getTourById(stage.tourId)?.matches(location.pathname)) {
      setDebug("journey-start:navigate", stage.nextPath);
      navigate(stage.nextPath);
    }

    return true;
  }, [clearJourneyState, clearJourneyTourStatuses, location.pathname, navigate, persistJourneyState, setDebug]);

  const resetTour = useCallback((tourId) => {
    setTourState((prev) => {
      const next = { ...prev };
      delete next[tourId];
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const resetJourney = useCallback((journeyId) => {
    clearJourneyTourStatuses(journeyId);
    clearJourneyState(journeyId);
  }, [clearJourneyState, clearJourneyTourStatuses]);

  const resetAllTourState = useCallback(() => {
    teardownTour();
    autoStartedRef.current = {};
    suppressStageActionRef.current = null;
    setTourState({});
    setJourneyState({});
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem(JOURNEY_STORAGE_KEY);
    localStorage.removeItem("typogram_tour_first_course_route");
    setDebug("tour-reset-all", "Cleared all guided tour state");
  }, [setDebug, teardownTour]);

  const replayCurrentTour = useCallback(() => {
    if (!currentTour) return;
    resetTour(currentTour.id);
    startTour(currentTour.id, { force: true });
  }, [currentTour, resetTour, startTour]);

  const handleJourneyStageDone = useCallback((journeyId, stageIndex) => {
    const journey = getJourneyById(journeyId);
    if (!journey) {
      return;
    }

    const stage = journey.stages[stageIndex];
    const nextIndex = stageIndex + 1;
    const suppressKey = `${journeyId}:${stageIndex}`;
    const shouldSuppressAction = suppressStageActionRef.current === suppressKey;
    if (shouldSuppressAction) {
      suppressStageActionRef.current = null;
    }

    if (!stage) {
      setDebug("journey-stage-done-miss", `${journeyId} @ ${stageIndex}`);
      return;
    }

    setDebug("journey-stage-done", `${journeyId} @ ${stageIndex} -> ${nextIndex}`);

    if (nextIndex >= journey.stages.length) {
      persistJourneyState(journeyId, {
        status: "completed",
        currentStageIndex: stageIndex,
      });
      window.setTimeout(() => {
        if (!shouldSuppressAction) {
          runJourneyStageAction(stage.onDoneAction);
        }
      }, 30);
      return;
    }

    if (stage.advanceOn !== "done") {
      return;
    }

    persistJourneyState(journeyId, {
      status: "active",
      currentStageIndex: nextIndex,
    });

    window.setTimeout(() => {
      if (stage.nextPath && location.pathname !== stage.nextPath) {
        setDebug("journey-stage-nextPath", stage.nextPath);
        navigate(stage.nextPath);
        return;
      }
      if (!shouldSuppressAction) {
        runJourneyStageAction(stage.onDoneAction);
      }
    }, 30);
  }, [location.pathname, navigate, persistJourneyState, runJourneyStageAction, setDebug]);

  useEffect(() => {
    if (!activeTourId || !driverRef.current?.isActive()) {
      return;
    }

    const activeTour = getTourById(activeTourId);
    if (!activeTour || activeTour.matches(location.pathname)) {
      return;
    }

    destroyReasonRef.current = "route-change";
    driverRef.current.destroy();
  }, [activeTourId, location.pathname]);

  useEffect(() => {
    if (!activeJourney) {
      return;
    }

    const { journey, state } = activeJourney;
    const matchingStageIndex = journey.stages.findIndex((stage) => {
      const stageTour = getTourById(stage.tourId);
      return stageTour?.matches(location.pathname);
    });

    if (matchingStageIndex > state.currentStageIndex) {
      persistJourneyState(journey.id, {
        status: "active",
        currentStageIndex: matchingStageIndex,
      });
      return;
    }

    const activeStage = journey.stages[state.currentStageIndex];
    const activeStageTour = getTourById(activeStage?.tourId);
    if (!activeStage || !activeStageTour?.matches(location.pathname)) {
      return;
    }

    const timer = window.setTimeout(async () => {
      setDebug("tour-start", `${journey.id} -> ${activeStage.tourId}`);
      await startTour(activeStage.tourId, {
        force: true,
        journeyId: journey.id,
        stageIndex: state.currentStageIndex,
        onDone: () => handleJourneyStageDone(journey.id, state.currentStageIndex),
      });
    }, 750);

    return () => window.clearTimeout(timer);
  }, [activeJourney, handleJourneyStageDone, location.pathname, persistJourneyState, setDebug, startTour]);

  useEffect(() => {
    if (featureFlagsLoading || !firstVisitToursEnabled || !currentTour || !currentTour.autoStart || activeJourney) {
      return;
    }

    const existing = tourState[currentTour.id];
    if (existing?.status === "seen" || existing?.status === "skipped") {
      return;
    }

    const tourAutoStartKey = `${location.pathname}:${currentTour.id}`;
    if (autoStartedRef.current[tourAutoStartKey]) {
      return;
    }

    const timer = window.setTimeout(async () => {
      const started = await startTour(currentTour.id);
      if (started) {
        autoStartedRef.current[tourAutoStartKey] = true;
      }
    }, 900);

    return () => window.clearTimeout(timer);
  }, [activeJourney, currentTour, featureFlagsLoading, firstVisitToursEnabled, location.pathname, startTour, tourState]);

  useEffect(() => {
    const eligibleJourney = TOUR_JOURNEYS.find(
      (journey) => journey.autoStart && journey.autoStartMatches?.(location.pathname)
    );
    if (!eligibleJourney || !currentUser || activeJourney) {
      return;
    }

    const search = new URLSearchParams(location.search);
    if (search.get("tour") === "0") {
      return;
    }

    const existing = journeyState[eligibleJourney.id];
    if (existing?.status === "completed" || existing?.status === "dismissed") {
      return;
    }

    if (autoStartedRef.current[location.pathname] === eligibleJourney.id) {
      return;
    }

    const timer = window.setTimeout(() => {
      const started = startJourney(eligibleJourney.id);
      if (started) {
        autoStartedRef.current[location.pathname] = eligibleJourney.id;
      }
    }, 900);

    return () => window.clearTimeout(timer);
  }, [activeJourney, currentUser, journeyState, location.pathname, location.search, startJourney]);

  const contextValue = useMemo(() => ({
    activeTourId,
    currentTour,
    startTour,
    startJourney,
    replayCurrentTour,
    resetTour,
    resetJourney,
    resetAllTourState,
    hasTourForCurrentRoute: Boolean(currentTour),
    currentTourStatus: currentTour ? tourState[currentTour.id]?.status || "new" : "unavailable",
    activeJourneyId: activeJourney?.journey.id || null,
    dismissActiveTour: teardownTour,
  }), [
    activeJourney,
    activeTourId,
    currentTour,
    replayCurrentTour,
    resetAllTourState,
    resetJourney,
    resetTour,
    startJourney,
    startTour,
    teardownTour,
    tourState,
  ]);

  return (
    <GuidedTourContext.Provider value={contextValue}>
      {children}
      {showDebugOverlay && activeJourney && (
        <div
          style={{
            position: "fixed",
            left: 12,
            bottom: 12,
            zIndex: 99999,
            width: 320,
            maxWidth: "calc(100vw - 24px)",
            background: "rgba(3, 7, 18, 0.92)",
            color: "#e5e7eb",
            border: "1px solid rgba(168, 85, 247, 0.35)",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div><strong>Journey:</strong> {activeJourney.journey.id}</div>
          <div><strong>Stage:</strong> {activeJourney.state.currentStageIndex}</div>
          <div><strong>Tour:</strong> {activeTourId || "none"}</div>
          <div><strong>Path:</strong> {location.pathname}</div>
          <div><strong>Last:</strong> {debugInfo.lastAction}</div>
          {debugInfo.detail ? <div><strong>Detail:</strong> {debugInfo.detail}</div> : null}
        </div>
      )}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour() {
  const value = useContext(GuidedTourContext);
  if (!value) {
    throw new Error("useGuidedTour must be used within GuidedTourProvider");
  }
  return value;
}
